// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// A LOCAL error boundary for the Investment Reporting island.
//
// WHY THIS EXISTS: there is no error boundary anywhere in src/ (verified 2026-08-11:
// zero matches for componentDidCatch / getDerivedStateFromError / ErrorBoundary across the
// whole tree, and react-error-boundary is not a dependency). React 18 unmounts the ENTIRE
// root tree on an uncaught render error, so a single bad shape returned by one RPC in one
// tab white-screens the whole SPA for that user — not just this island. Any tab that renders
// server-controlled jsonb therefore has to contain its own blast radius.
//
// This is deliberately NOT a global boundary: swallowing render errors app-wide would hide
// real bugs from everyone. It is scoped to one island so that a failure here degrades to a
// readable card while the surrounding shell — the cycle header, the tab strip, the rest of
// the app — keeps working.
//
// HONESTY: this renders an explicit failure. It never renders a plausible-looking empty
// state that could be mistaken for "nothing to review", because the difference between
// "this control found nothing" and "this control crashed" is exactly the difference that
// matters on a surface whose job is to gate a retail financial promotion.

import { Component, type ErrorInfo, type ReactNode } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
  /** Names the surface in the fallback copy, e.g. "the newsletter review tab". */
  surface?: string;
  /** Extra context rendered under the message — e.g. what is known to be unbuilt. */
  detail?: ReactNode;
}

interface State {
  error: Error | null;
}

export class ReportingErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Keep the stack reachable for whoever is debugging, without surfacing it to the user.
    console.error("[investment-reporting] render error contained by boundary", error, info);
  }

  private reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const surface = this.props.surface ?? "this surface";

    return (
      <Card className="p-6 space-y-3">
        <div className="flex items-start gap-2">
          <AlertTriangle
            className="h-5 w-5 mt-0.5 shrink-0 text-destructive"
            aria-hidden
          />
          <div className="space-y-1 min-w-0">
            <div className="font-heading text-sm font-medium text-foreground">
              Something went wrong rendering {surface}.
            </div>
            <p className="font-body text-xs text-muted-foreground">
              This is a fault in the page, not a statement about the newsletter. Nothing has
              been reviewed, approved or sent as a result of this error, and no review can be
              recorded while this is showing. The rest of the page is unaffected.
            </p>
            {this.props.detail}
            <p className="font-body text-[11px] text-muted-foreground break-words pt-1">
              <span className="font-medium text-foreground">Details:</span>{" "}
              {error.message || String(error)}
            </p>
          </div>
        </div>
        <div>
          <Button variant="outline" size="sm" onClick={this.reset}>
            <RefreshCw className="h-3.5 w-3.5 mr-2" aria-hidden /> Try again
          </Button>
        </div>
      </Card>
    );
  }
}
